import React from 'react';
import DocumentPage, {
  Section,
  SubSection,
  Paragraph,
  BulletList,
  OrderedList,
  HazardTable,
  Callout,
} from './DocumentPage';
import CalorVesselsNotice from './CalorVesselsNotice';

const DeGassingFlareDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="De-Gassing and Flare Operations – Risk Assessment"
    documentHeading="Risk Assessment: De-Gassing and Flare Operations"
    meta={[
      ['Document Reference', 'PVS-DGF-001'],
      ['Revision', '1.0'],
      ['Date of Issue', '17/07/2026'],
      ['Review Period', 'Every 3 years, or on review of the DSEAR Assessment (whichever is sooner)'],
      ['Next Review Date', 'May 2028 (aligned to DSEAR Assessment review)'],
    ]}
    acknowledgment={{
      documentName: 'De-Gassing and Flare Operations',
      documentReference: 'PVS-DGF-001',
      processActivity: 'De-Gassing of LPG Vessels and Flare Stack Operations',
      versionNumber: '1.0',
      dateOfIssue: '17/07/2026',
    }}
    onReturnHome={onReturnHome}
  >
    <CalorVesselsNotice />

    <Section number="1" title="Introduction and Scope">
      <Paragraph>
        This Risk Assessment is designed for use by Pressure Vessel Services Limited employees when
        carrying out de-gassing of returned LPG storage vessels and the associated burn-off of
        residual vapour via the LPG-fired pyrolysis oven and the gas flare stack at the company's
        premises. It covers connection of vessels to the vapour manifold, transfer of residual
        vapour to the oven, burn-off of vapour below 2 bar at the flare stack, atmosphere monitoring
        using the Gas Monitor, and formal confirmation of gas-free status via the Gas Free Check
        form (PVS-GFC-001). This document aims to identify potential hazards and outline control
        measures to ensure compliance with the Health and Safety at Work Act 1974, the Dangerous
        Substances and Explosive Atmospheres Regulations 2002 (DSEAR), the Pressure Systems Safety
        Regulations 2000 (PSSR), and other relevant UK legislation.
      </Paragraph>
      <Paragraph className="mt-3">
        <strong>This Risk Assessment must be read in conjunction with the company DSEAR
        Assessment</strong> (Pressure Vessel Services Ltd – DSEAR Assessment, PIB Risk Management,
        assessed by Kevin Barrett CMIOSH, dated 8th May 2026). The DSEAR Assessment classifies the
        hazardous areas relevant to this activity as follows:
      </Paragraph>
      <BulletList
        className="mt-3"
        items={[
          <><strong>Inside returned vessels</strong> (containing residual liquid or vapour): <strong>DSEAR Zone 0</strong>.</>,
          <><strong>Around returned vessels awaiting refurbishment:</strong> <strong>DSEAR Zone 2</strong> (1m around individual tanks; up to 3m in bulk storage areas).</>,
          <><strong>Gas flare stack</strong> (open field directly adjacent to the premises): <strong>DSEAR Zone 1</strong>.</>,
          <><strong>Gas pipework feeding the oven</strong> from external bulk tanks: <strong>DSEAR Zone 2</strong>.</>,
          <><strong>Bulk LPG storage tanks feeding the oven:</strong> <strong>DSEAR Zone 0</strong>.</>,
        ]}
      />
      <Paragraph className="mt-3">
        <strong>This document is a general risk assessment for routine de-gassing and flare
        operations.</strong> Employees are responsible for conducting a visual inspection prior to
        commencing work for any additional dangers or unforeseen risks that may cause harm to
        themselves, colleagues, the vessel, or equipment. Risks are not limited to those listed
        below, and if it is the feeling of the employees that the particular job in question
        requires a further detailed, custom risk assessment, then it shall be done and approved by a
        Senior Engineer or Management.
      </Paragraph>
    </Section>

    <Section number="2" title="General Information">
      <BulletList
        items={[
          <><strong>Company:</strong> Pressure Vessel Services Limited</>,
          <><strong>Location:</strong> Company premises – new production shed (vapour manifold / pyrolysis oven) and adjacent open field (gas flare stack).</>,
          <><strong>Activity:</strong> De-gassing of returned LPG vessels – removal of residual vapour via the vapour manifold to the pyrolysis oven (above 2 bar) and controlled burn-off at the flare stack (below 2 bar), including atmosphere monitoring and gas-free verification.</>,
          <><strong>Key Personnel:</strong> Trained and authorised engineers; Health and Safety Officer (Senior Engineer on duty).</>,
          <><strong>Equipment:</strong> Vapour manifold and hoses, LPG-fired pyrolysis oven, gas flare stack with pilot light, flash back arrestor, calibrated Gas Monitor, approved Leak Detection Fluid (LDF), dry powder fire extinguishers.</>,
        ]}
      />
      <SubSection title="Reference Documents">
        <BulletList
          items={[
            'Pressure Vessel Services Ltd – DSEAR Assessment (PIB Risk Management, Kevin Barrett CMIOSH, 8th May 2026), including the Hazardous Area Classification (HAC) Plan',
            'PVS Health And Safety Policy 2025',
            'Written Scheme of Examination – Sections 7 (Vapour Removal), 8 (Valve Removal) and 9 (Natural Air Purge)',
            'PVS-RAMS-VEP-001 – Vessel Inspection and Preparation Method Statement',
            'PVS-RAMS-PYR-001 – Pyrolysis Work Flow',
            'PVS-GFC-001 – Gas Free Check Form',
            'Gas Monitor Use & Bump Test Work Instruction',
            'LPGA Code of Practice 1 Part 3',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="3" title="Responsibilities">
      <Paragraph><strong>Overall Responsibility:</strong> The Board of Directors of the Employer.</Paragraph>
      <Paragraph className="mt-2"><strong>Day-to-Day Responsibility:</strong> Health and Safety Officer (Senior Engineer on duty).</Paragraph>
      <SubSection title="Operator Responsibilities">
        <BulletList
          items={[
            'Only trained and authorised personnel may connect vessels to the vapour manifold, operate the oven feed, or light and operate the flare stack.',
            'Complete the pre-operation leak checks and pilot light confirmation in Section 5 before releasing any vapour.',
            'Verify the flash back arrestor is fitted and in serviceable condition before igniting the flare.',
            'Ensure the Gas Monitor has passed its daily bump test / calibration check before use.',
            'Maintain regular monitoring of the flare at all times while it is lit; never leave a lit flare unattended for extended periods.',
            'Complete the Gas Free Check form (PVS-GFC-001) with the recorded LEL reading before the vessel is signed off as gas free.',
            'Report all health and safety concerns, defects, incidents, or near misses promptly to the Health and Safety Officer.',
            'Ensure no unauthorised persons or contractors enter the zoned areas while de-gassing or flaring is in progress (per the DSEAR Assessment contractor control requirements).',
          ]}
        />
      </SubSection>
      <SubSection title="Other Employee Responsibilities (working near de-gassing / flare operations)">
        <BulletList
          items={[
            'Remain outside the designated DSEAR zones unless authorised and directly involved in the operation.',
            'Do not introduce any source of ignition (smoking, hot work, non-ATEX electrical equipment, vehicles) within the zoned areas.',
            'Report any smell of gas, visible leak, or extinguished flame immediately.',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="4" title="Hazard Identification and Risk Assessment">
      <HazardTable
        columns={['Hazard', 'Potential Harm', 'Existing Controls', 'Residual Risk']}
        rows={[
          [
            'LPG Vapour Leak at Oven / Manifold Connections',
            'Formation of explosive atmosphere, fire, explosion, serious injury, fatality.',
            [
              'All oven and manifold connections leak checked using approved Leak Detection Fluid (LDF) before and after each vessel connection.',
              'Connections re-checked whenever a joint is broken and remade.',
              'Hoses and fittings visually inspected before use; defective items removed from service.',
              'Area atmosphere monitored with calibrated Gas Monitor during operations.',
              'Work stopped and supply isolated immediately if any leak is detected.',
            ],
            'Low',
          ],
          [
            'Unburnt Gas Release at Flare (Pilot Light Out)',
            'Accumulation of unburnt LPG vapour, delayed ignition, vapour cloud fire/explosion.',
            [
              'Pilot light visually confirmed as lit BEFORE any vapour is released to the flare.',
              'Regular monitoring of the flare while lit; flame status checked at defined intervals throughout the burn.',
              'If the pilot or main flame extinguishes, gas supply isolated immediately and area allowed to disperse before re-ignition.',
              'Flare stack located in an open field adjacent to the premises (DSEAR Zone 1), aiding natural dispersion.',
            ],
            'Low',
          ],
          [
            'Flash Back Along Vapour Line',
            'Flame propagation back to the vessel or manifold, explosion, rupture.',
            [
              'Flash back arrestor confirmed in place and serviceable before the flare is ignited.',
              'Flash back arrestor inspected regularly and maintained in accordance with manufacturer instructions.',
              'Vapour only routed to the flare when vessel pressure is below 2 bar (WSE Section 7).',
            ],
            'Low',
          ],
          [
            'Explosive Atmosphere Around Vessel / Work Area',
            'Fire, explosion, serious injury, fatality.',
            [
              'Hazardous areas classified and controlled per the DSEAR Assessment (Zone 0 inside vessels; Zone 2 around returned vessels; Zone 1 at flare stack).',
              'Atmosphere monitored using the calibrated Gas Monitor; work is stopped and the area evacuated if the ambient reading reaches 10% LEL – well below the 25% LEL maximum working threshold stated in the DSEAR Assessment.',
              'No ignition sources permitted within zoned areas; ATEX-rated equipment only.',
              'Anti-static clothing / footwear worn in zoned areas as required by the DSEAR Assessment.',
              'Operations carried out in well-ventilated / open-air locations.',
            ],
            'Low',
          ],
          [
            'Vessel Incorrectly Declared Gas Free',
            'Fire/explosion during subsequent hot work, stud welding, pyrolysis or valve installation.',
            [
              'Vessel atmosphere measured with a calibrated Gas Monitor after de-gassing and the minimum 48-hour natural air purge (WSE Section 9).',
              'Vessel declared Gas Free only when the reading is below 1% LEL, in line with LPGA CoP 1 Part 3 and PVS-RAMS-VEP-001.',
              'Result formally recorded on the Gas Free Check form (PVS-GFC-001) – LEL value, date/time, instrument serial number and operator signature – to signify the status of the vessel.',
              'If the reading is ≥ 1% LEL, venting continues and the vessel is retested; no progression to hot work, ultrasonic examination or re-valving until a < 1% LEL result is recorded.',
              'Gas Monitor must have passed its daily bump test and calibration check before use.',
            ],
            'Low',
          ],
          [
            'Residual Pressure / Stored Energy in Vessel',
            'Uncontrolled release, striking injuries, whipping hoses.',
            [
              'Vessel pressure confirmed on gauge before connection or disconnection.',
              'Vapour above 2 bar routed to the oven via the manifold; only below 2 bar routed to the flare (WSE Section 7).',
              'Hoses vented in a controlled manner before disconnection.',
              'Valves confirmed isolated before breaking any joint.',
            ],
            'Low',
          ],
          [
            'Burns / Radiant Heat from Flare',
            'Burns to skin, heat stress, ignition of nearby combustibles.',
            [
              'Safe standoff distance maintained from the lit flare.',
              'Area around the flare kept clear of combustible materials and vegetation.',
              'Appropriate PPE worn (gloves, flame-resistant clothing where required).',
              'Dry powder fire extinguishers positioned within easy reach.',
            ],
            'Low',
          ],
          [
            'Unauthorised Persons / Contractors in Zoned Areas',
            'Introduction of ignition sources, persons exposed to fire/explosion risk.',
            [
              'Unauthorised persons prohibited from zoned areas; access controlled during operations.',
              'Contractors working around LPG vessels controlled per the DSEAR Assessment recommendation (permit-to-work arrangements).',
              'Warning signage displayed at the flare area and vessel storage areas.',
            ],
            'Low',
          ],
          [
            'Lack of Competence / Training',
            'Incorrect procedures, accidents, equipment damage, non-compliance.',
            [
              'Only trained and authorised personnel carry out de-gassing and flare operations.',
              'DSEAR-specific training provided on precautions and actions required in zoned areas, per the DSEAR Assessment key actions.',
              'Training on Gas Monitor use and daily bump testing.',
              'Competence reviewed periodically.',
            ],
            'Low',
          ],
          [
            'Emergency Situation',
            'Injury, fatality, delayed response, inadequate evacuation.',
            [
              'Emergency isolation points known to all operators; gas supply can be isolated rapidly.',
              'Immediate evacuation and notification of lead engineers / management in an emergency.',
              'Dry powder fire extinguishers within easy reach.',
              'Site emergency procedures communicated to all personnel; prompt reporting of accidents.',
            ],
            'Low',
          ],
        ]}
      />
    </Section>

    <Section number="5" title="Safe System of Work (SSoW) – De-Gassing and Flare Operations">
      <SubSection title="5.1 Pre-Operation Checks">
        <OrderedList
          items={[
            <><strong>Gas Monitor:</strong> Confirm the Gas Monitor has passed its daily bump test and calibration check (per the Gas Monitor Use &amp; Bump Test Work Instruction) before any de-gassing work commences.</>,
            <><strong>Area Survey:</strong> Visually inspect the work area and flare stack area; confirm no unauthorised persons, no ignition sources, and no combustible materials within the zoned areas.</>,
            <><strong>Equipment Inspection:</strong> Visually inspect hoses, manifold fittings, valves and the flash back arrestor for damage or wear. Defective equipment shall not be used.</>,
            <><strong>Flash Back Arrestor:</strong> Confirm the flash back arrestor is fitted in the flare supply line and is in serviceable condition. <strong>The flare shall not be ignited without a serviceable flash back arrestor in place.</strong></>,
            <><strong>Fire Precautions:</strong> Confirm dry powder fire extinguishers are positioned within easy reach of both the oven connection area and the flare stack.</>,
          ]}
        />
      </SubSection>

      <SubSection title="5.2 Vapour Transfer to Oven (Vessel Pressure > 2 bar)">
        <OrderedList
          items={[
            'Confirm vessel pressure on the gauge.',
            'Connect the vessel to the vapour manifold feeding the pyrolysis oven.',
            <><strong>Leak check all oven and manifold connections using approved Leak Detection Fluid (LDF)</strong> before opening any valve fully. Any leak shall result in immediate isolation and rectification before proceeding.</>,
            'Open valves in a slow, controlled manner and re-check joints with LDF under pressure.',
            'Transfer residual vapour to the oven until vessel pressure falls below 2 bar (WSE Section 7).',
            <>Monitor the area atmosphere with the Gas Monitor throughout; stop work and isolate if the ambient reading reaches <strong>10% LEL</strong>.</>,
          ]}
        />
      </SubSection>

      <SubSection title="5.3 Burn-Off at Flare Stack (Vessel Pressure < 2 bar)">
        <OrderedList
          items={[
            'Connect the vessel to the flare stack line; leak check connections with LDF.',
            <><strong>Confirm the pilot light is lit</strong> before releasing any vapour to the flare. Do not open the vapour supply to an unlit flare.</>,
            'Open the supply in a controlled manner and confirm stable ignition of the main flame.',
            <><strong>Regularly monitor the flare while it is lit:</strong> confirm the flame remains established, watch for flame-out, abnormal flame behaviour or wind effects, and monitor the surrounding atmosphere with the Gas Monitor.</>,
            'If the flame extinguishes at any point, isolate the gas supply immediately, allow vapour to disperse, confirm the area reads below 10% LEL, then re-establish the pilot before resuming.',
            'Continue burn-off until the vessel is at atmospheric pressure and vapour flow has ceased.',
            'Isolate all valves, vent trapped gas from hoses in a controlled and safe manner, and disconnect.',
          ]}
        />
      </SubSection>

      <SubSection title="5.4 Gas-Free Verification and Sign-Off">
        <OrderedList
          items={[
            'Following valve removal, the vessel shall undergo the minimum 48-hour natural air purge per WSE Section 9 and PVS-RAMS-VEP-001.',
            'Measure the atmosphere inside the vessel using the calibrated Gas Monitor.',
            <>The vessel shall be considered <strong>Gas Free only when the reading is below 1% LEL</strong>. The lower explosive limit for LPG is 2% gas in air (GIA); 1% LEL therefore corresponds to just 0.02% GIA, providing a wide margin of safety consistent with LPGA CoP 1 Part 3 and well below the 25% LEL maximum working threshold stated in the DSEAR Assessment guidance.</>,
            <><strong>Complete the Gas Free Check form (PVS-GFC-001)</strong> recording the LEL value, date/time of test, instrument serial number and operator signature. Completion of this form signifies the gas-free status of the vessel and shall be retained as part of the vessel certification record.</>,
            <>If the reading is ≥ 1% LEL, continue natural venting and retest. The vessel shall not progress to hot work, pyrolysis, stud work, ultrasonic examination or re-valving until a &lt; 1% LEL result has been recorded on PVS-GFC-001.</>,
          ]}
        />
      </SubSection>

      <SubSection title="5.5 Monitoring Action Levels (Summary)">
        <HazardTable
          columns={['Reading (Gas Monitor)', 'Status', 'Action']}
          rows={[
            [
              '< 1% LEL inside vessel',
              'Gas Free',
              'Complete Gas Free Check form (PVS-GFC-001); vessel may progress to next process stage.',
            ],
            [
              '≥ 1% LEL inside vessel',
              'Not gas free',
              'Continue natural venting and retest. Do not progress to hot work, pyrolysis, stud work, ultrasonic examination or re-valving.',
            ],
            [
              '≥ 10% LEL (ambient work area)',
              'Stop work',
              'Isolate gas supply, evacuate the immediate area, eliminate ignition sources, allow dispersion, notify Senior Engineer. Do not resume until below 10% LEL.',
            ],
            [
              '≥ 25% LEL (ambient work area)',
              'DSEAR maximum threshold exceeded',
              'Emergency response: full evacuation of area, isolate all supplies, notify management; treat as a dangerous occurrence and investigate before any restart.',
            ],
          ]}
        />
      </SubSection>
    </Section>

    <Section number="6" title="Personal Protective Equipment (PPE)">
      <Paragraph>
        Appropriate PPE shall be worn at ALL times during de-gassing and flare operations. This
        includes, but is not limited to:
      </Paragraph>
      <BulletList
        className="mt-3"
        items={[
          'Hi-visibility Jacket/Vest.',
          'Appropriate footwear (rubber soled, steel toe-capped; anti-static footwear when working within DSEAR zoned areas, per the DSEAR Assessment).',
          'Anti-static clothing when working within zoned areas.',
          'Gloves suitable for the task (thermal protection when working near the flare).',
          'Eye protection (safety glasses/goggles).',
          'Hearing protection during venting or compressor operations.',
        ]}
      />
    </Section>

    <Section number="7" title="Training and Competence">
      <BulletList
        items={[
          'Only trained and authorised personnel may carry out de-gassing and flare operations.',
          'DSEAR awareness – hazardous zones, prohibited activities in zoned areas, and required precautions (per the DSEAR Assessment key action on DSEAR training).',
          'Vapour manifold and flare stack operation, including pilot light confirmation and flame monitoring.',
          'Leak checking with Leak Detection Fluid (LDF).',
          'Function and inspection of the flash back arrestor.',
          'Gas Monitor use, daily bump test and interpretation of LEL readings.',
          'Completion of the Gas Free Check form (PVS-GFC-001).',
          'Emergency procedures, including gas isolation, fire response and first aid.',
          'Competence depends on a combination of training, experience, and formal assessment, and will be reviewed periodically.',
        ]}
      />
    </Section>

    <Section number="8" title="Emergency Procedures">
      <BulletList
        items={[
          <><strong>Gas Leak (Unignited):</strong> Isolate the supply at the nearest isolation point, eliminate ignition sources, evacuate the immediate area, and allow the vapour to disperse. Confirm the atmosphere is below 10% LEL with the Gas Monitor before re-entry. Notify the Health and Safety Officer.</>,
          <><strong>Fire / Explosion:</strong> Activate the nearest fire alarm, evacuate the vicinity, and notify the lead engineers or management on site. Only attempt to tackle a fire if trained and competent and it is safe to do so.</>,
          <><strong>Flame-Out at Flare:</strong> Isolate the gas supply immediately. Do not attempt re-ignition until the surrounding atmosphere has dispersed and reads below 10% LEL.</>,
          <><strong>Accidents / Injuries:</strong> Promptly report any accident, however trivial, to the Health and Safety Officer for recording in the Accident Book. In a serious accident, dial 999 giving name, location, and brief details. RIDDOR reporting by the Health and Safety Officer where required.</>,
        ]}
      />
      <Callout tone="danger">
        Do not attempt to extinguish a gas flame unless the supply can be isolated – extinguishing
        without isolation creates an explosive vapour release.
      </Callout>
    </Section>

    <Section number="9" title="Environmental Considerations">
      <BulletList
        items={[
          'Residual vapour is disposed of by controlled combustion (oven / flare stack) rather than venting to atmosphere, minimising environmental release.',
          'The flare area shall be kept clear of vegetation and combustible material.',
          'Report any uncontrolled release immediately.',
        ]}
      />
    </Section>

    <Section number="10" title="Review">
      <Paragraph>
        This Risk Assessment will be reviewed at regular intervals to ensure that it is achieving
        its aims effectively. It will be reviewed by the Board of Directors and amended
        appropriately with careful consideration and consultation with all relevant parties. It
        shall also be reviewed following any incident, near miss, change in equipment or process, or
        upon reissue of the DSEAR Assessment (next DSEAR review due May 2028).
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default DeGassingFlareDoc;
